import { Component, ViewChild, effect, inject, input, output, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AgGridAngular } from "ag-grid-angular";
import { ColDef, ColTypeDef, GridOptions } from "ag-grid-community";
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { create, all } from 'mathjs';
import { EmissionSourceFormulaModel, FormulaSearchModel } from '@features/forms/models/emission-source.model';
import { FactorSelect } from '../factor-select/factor-select';
import { HeaderHtmlRenderer } from './header-html-renderer';
import { EmissionsFactoryForFormModel, GasesModel } from '@features/emissions/models/emissions-factory.model';
import { GasPotential, GlobalWarmingPotentialModel } from '@features/forms/models/global-warming-potential.model';
import { ConversionModel, ValueModel } from '@features/forms/models/conversion.model';
import { ActivitiesDefinitionModel, ActivitiesModel, ActivitiesRowModel } from '@features/forms/models/activities.model';
import { CellContextMenuEvent } from 'ag-grid-community';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';

type GasPotentialField = 'value' | 'uncertainty';
type ConversionField = 'value';

@Component({
  selector: 'app-form-builder',
  imports: [
    AgGridAngular,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    MessageModule,
    ContextMenu
  ],
  templateUrl: './form-builder.html',
  styleUrl: './form-builder.scss',
})
export class FormBuilder {
  @ViewChild('cm') contextMenu!: ContextMenu;
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  private readonly http = inject(HttpClient);

  config = {}
  math = create(all, this.config)

  private headersMap: Record<string, string> = {};
  private equipmentMap: Record<string, string> = {};

  formula = input<EmissionSourceFormulaModel>();
  activities = input<ActivitiesModel>();
  filters = input<FormulaSearchModel>();
  loading = input<boolean>(false);
  emissionFactorOptions = input<EmissionsFactoryForFormModel[]>([]);
  globalWarmingPotentialOptions = input<GlobalWarmingPotentialModel[]>([]);
  conversionOptions = input<ConversionModel[]>([]);

  // Output para emitir cambios en los datos de la tabla
  tableDataChange = output<any[]>();
  openUploadFilesDialog = output<{activity: ActivitiesRowModel, open: boolean}>();
  deleteActivity = output<ActivitiesRowModel>();

  columnDefs: ColDef[] = [];
  rowHeight = 65;

  defaultColDef: ColDef = { flex: 1, enableCellChangeFlash: true, headerComponent: HeaderHtmlRenderer };
  rowData: any[] | null = null;
  columnTypes: { [key: string]: ColTypeDef } = {
    valueColumn: {
      editable: true,
    },
  };

  conversionFieldMap: Record<
    ConversionField,
    keyof ValueModel
  > = {
    value: 'Value'
  };

  gasFieldMap: Record<GasPotentialField, keyof GasPotential> = {
    value: 'Value',
    uncertainty: 'Uncertainty'
  };

  gridOptions: GridOptions = {} as GridOptions;
  private isGridReady = false;

  selectedRow: ActivitiesRowModel = {} as ActivitiesRowModel;
  contextMenuItems: MenuItem[] = [
    {
      label: 'Anexos',
      icon: 'pi pi-copy',
      command: () => this.openUploadFiles(this.selectedRow)
    }
  ];

  constructor() {
    // Manejar valores null o undefined evitando colapso en la evaluación de mathjs
    this.math.import({
      equalText: (a: any, b: any) => {
        const strA = a === null || a === undefined ? '' : String(a);
        const strB = b === null || b === undefined ? '' : String(b);
        return strA.toLowerCase() === strB.toLowerCase();
      }
    }, { override: true });

    // Cargar diccionario de headers
    this.loadHeadersMap();
    this.loadEquipmentMap();

    // Generar columnDefs cuando cambia el input formula
    effect(() => {
      this.columnDefs = this.generateColumnDefs();
    });

    // Regenerar rowData cuando cambian activities
    effect(() => {
      if (this.formula()?.Id) {
        this.rowData = this.generateRowData(this.formula());

        // Pre-calcular ResultDefinition para que los valores estén disponibles desde el inicio
        this.preCalculateResultDefinitions(this.formula()!, this.rowData);

        // Recalcular todas las columnas cuando se agrega una nueva activity
        queueMicrotask(() => this.forceCalculateAllRows());
      }
    });

    // Recalcular fórmulas cuando cambia el GWP (pcg) en los filtros
    effect(() => {
      const pcg = this.filters()?.pcg;
      // Solo recalcular si hay datos y el grid está listo
      if (pcg && this.isGridReady && this.agGrid?.api && this.rowData && this.rowData.length > 0) {

        // Recalcular todas las columnas cuando se selecciona otro PCG
        queueMicrotask(() => this.forceCalculateAllRows());
      }
    });

    // Controlar el overlay de carga cuando cambia el estado de loading
    effect(() => {
      const isLoading = this.loading();
      this.updateLoadingState(isLoading);
    });

    this.gridOptions = {
      tooltipShowDelay: 0,
      overlayLoadingTemplate: '<div style="padding: 20px; text-align: center;"><div style="display: inline-block; width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite;"></div><div style="margin-top: 10px;">Cargando...</div></div><style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>',
      overlayNoRowsTemplate: 'No hay información para mostrar',
      suppressMovableColumns: true,
      suppressRowDrag: true,
      onGridReady: () => {
        this.isGridReady = true;
        // Cuando el grid esté listo, establecer el estado inicial de loading
        this.updateLoadingState(this.loading());
      },
      onCellValueChanged: (event: any) => {
        // Recalcular formulas para esta fila de forma secuencial antes de refrescar la vista
        if (this.formula()) {
          this.preCalculateResultDefinitions(this.formula()!, [event.data]);
        }

        // Al emitir los datos, nos aseguramos que las celdas de cálculo se refresquen
        if (this.isGridReady && this.agGrid?.api) {
          this.agGrid.api.refreshCells({ rowNodes: [event.node], force: true });
        }
        // Emitir los datos actuales de la tabla cuando cambie algún valor
        this.tableDataChange.emit(this.rowData || []);
      }
    };
  }

  /**
   * Carga el diccionario de headers para traducir los códigos de columna
   */
  private loadHeadersMap(): void {
    this.http.get<Record<string, string>>('assets/data/headers.json').subscribe({
      next: (map) => {
        this.headersMap = map;
        // Si ya hay fórmula cargada, regenerar las columnas con los headers traducidos
        if (this.formula()?.Id) {
          this.columnDefs = this.generateColumnDefs();
        }
      },
      error: () => {
        this.headersMap = {};
      }
    });
  }

  /**
   * Obtiene el header traducido del diccionario o retorna el Code original
   */
  private getHeaderLabel(code: string): string {
    return this.headersMap[code] || code;
  }

  /**
   * Calcula el ancho mínimo basado únicamente en la longitud del texto del header
   */
  private calculateMinWidth(headerName: string, baseMinWidth: number = 120): number {
    const cleanHeaderName = headerName ? headerName.replace(/<[^>]*>?/gm, '') : '';
    return Math.max(baseMinWidth, cleanHeaderName.length * 9 + 45);
  }

  /**
   * Obtiene las opciones de ancho de columna, considerando casos de ancho fijo requeridos
   */
  private getColumnWidthOptions(def: any, headerName: string, baseMinWidth: number = 120): any {
    if (def.Code === 'GREENHOUSE_GAS' || def.Code === 'EQUIPMENT' || def.Code === 'VEHICLE') {
      return { flex: 0, width: 330, minWidth: 330 };
    }
    if (def.Code === 'TOTAL') {
      return { flex: 0, width: 200, minWidth: 200 };
    }
    return { minWidth: this.calculateMinWidth(headerName, baseMinWidth) };
  }  
  
  /**
   * Carga el diccionario de equipos para traducir los valores de la columna EQUIPMENT
   */
  private loadEquipmentMap(): void {
    this.http.get<Record<string, string>>('assets/data/equipment.json').subscribe({
      next: (map) => {
        this.equipmentMap = map;
        // Si ya hay fórmula cargada, regenerar las columnas con los valores traducidos
        if (this.formula()?.Id) {
          this.columnDefs = this.generateColumnDefs();
        }
      },
      error: () => {
        this.equipmentMap = {};
      }
    });
  }

  /**
   * Actualiza el estado de loading del grid
   */
  private updateLoadingState(isLoading: boolean): void {
    if (this.isGridReady && this.agGrid?.api) {
      this.agGrid.api.setGridOption('loading', isLoading);
    }
  }

  public stopEditing(): void {
    if (this.isGridReady && this.agGrid?.api) {
      this.agGrid.api.stopEditing();
    }
  }

  get currentGridData(): any[] {
    const data: any[] = [];
    if (this.isGridReady && this.agGrid?.api) {
      this.agGrid.api.stopEditing();
      this.agGrid.api.forEachNode(node => {
        if (node.data) data.push(node.data);
      });
      return data;
    }
    return this.rowData || [];
  }

  forceCalculateAllRows(pcg?: string): ActivitiesRowModel[] | undefined {
    if (!this.agGrid?.api) return;

    pcg = pcg ?? this.filters()!.pcg;

    const formula = this.formula();
    const activities = this.activities();

    if (!formula || !activities?.Rows) return;

    const activityCodes = formula.ActivityDefinition?.map(d => d.Code) || [];
    const measurementCodes = formula.MeasurementDefinition?.map(d => d.Code) || [];
    const resultDefs = formula.ResultDefinition || [];

    const allCodes = [
      ...activityCodes,
      ...measurementCodes,
      ...resultDefs.map(d => d.Code)
    ];

    this.agGrid.api.forEachNode((node) => {
      const row = node.data;

      if (!row) return;

      const activityRow = activities.Rows.find((_, index) => index === node.rowIndex);
      if (!activityRow) return;

      // Calcular fórmulas de ResultDefinition usando rawValues para preservar precisión completa
      const rawValues: Record<string, unknown> = {};
      for (const def of resultDefs) {
        if (def.Default && def.Default !== 'empty') {
          rawValues[def.Code] = def.Default;
          row[def.Code] = def.Default;
          continue;
        }
        const calcData = this.buildCalcData(row as Record<string, unknown>, rawValues);
        rawValues[def.Code] = this.evaluateFormula(this.stripRoundFunctions(def.Formula), calcData, pcg);
        row[def.Code] = this.evaluateFormula(def.Formula, calcData, pcg);
      }

      // Copiar el resto de columnas de Activity.Rows
      if (!activityRow.Definition) {
        activityRow.Definition = {} as ActivitiesDefinitionModel;
      }
      if (!activityRow.Measurements) {
        activityRow.Measurements = {} as any;
      }

      const measurementCodesSet = new Set(measurementCodes);

      allCodes.forEach(code => {
        if (measurementCodesSet.has(code)) {
          (activityRow.Measurements as any)[code] = row[code] ?? "";
        } else {
          activityRow.Definition![code as keyof ActivitiesDefinitionModel] = row[code] ?? "";
        }
      });
    });

    this.agGrid.api.refreshCells({ force: true });

    return activities.Rows;
  }

  generateRowData(formula?: EmissionSourceFormulaModel): any[] {
    if (formula !== undefined) {
      const activities = this.activities();

      // Si hay activities con Rows, mapear las filas existentes
      if (activities?.Rows && activities.Rows.length > 0) {
        return activities.Rows.map(activityRow => this.mapActivityRowToTableRow(activityRow, formula));
      }
    }

    // Si no hay actividades o fórmula, devolver array vacío
    return [];
  }

  generateColumnDefs() {
    const columnDefs: ColDef[] = [];

    if (!this.formula()?.Id) {
      return columnDefs;
    }

    const formula = this.formula()!;

    // Generar rowData basado en activities.Rows si está disponible
    this.rowData = this.generateRowData(formula);

    // Pre-calcular ResultDefinition en orden secuencial para que los valores estén
    // disponibles en params.data desde el primer renderizado del grid
    this.preCalculateResultDefinitions(formula, this.rowData);

    // Agregar columna visual de índice de fila
    columnDefs.push({
      headerName: 'N°',
      valueGetter: 'node.rowIndex + 1',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      editable: false,
      pinned: 'left',
      suppressMovable: true,
      cellStyle: { backgroundColor: '#f0f0f0', textAlign: 'center', fontWeight: 'bold' }
    });

    // Agregar columnas de ActivityDefinition
    if (formula.ActivityDefinition) {
      formula.ActivityDefinition.forEach(def => {
        const headerName = this.getHeaderLabel(def.Code);
        const colDef: ColDef = {
          field: def.Code,
          headerName: headerName,
          type: 'valueColumn',
          ...this.getColumnWidthOptions(def, headerName, 120),
          editable: true,
          cellEditorParams: {
            getValidationErrors: (params: any) => {
                const { value } = params;

                if (def.Type === 'percentage_string') {
                  const error = this.validatePercentageString(value);
                  if (error) return error;
                }

                if (def.Required && !value) {
                  return ['Campo requerido.'];
                }

                return null;
            },
          },
          valueFormatter: (params: any) => {
            if (def.Type === 'boolean') return this.formatBooleanValue(params.value);
            if (def.Code === 'EQUIPMENT' && params.value && this.equipmentMap[params.value]) {
              return this.equipmentMap[params.value];
            }
            return this.setPercentageValue(params.value, def.Type);
          }
        };

        if (def.Items) {
          if (Array.isArray(def.Items)) {
            colDef.cellEditor = 'agSelectCellEditor';
            colDef.cellEditorParams = {
              ...colDef.cellEditorParams,
              values: def.Items,
            };
            if (def.Code === 'EQUIPMENT') {
              colDef.cellEditorParams.valueListFormatter = (params: any) => {
                return this.equipmentMap[params.value] || params.value;
              };
            }
          } else if (typeof def.Items === 'object') {
            const itemsMap: Record<string, string[]> = (def.Items as any).Items || def.Items;
            
            // Verificamos que tenga las llaves GAS, SOLID, LIQUID
            if (Object.keys(itemsMap).length > 0) {
              colDef.cellEditor = 'agSelectCellEditor';
              colDef.cellEditorParams = (params: any) => {
                // Obtener el factor seleccionado de la fila
                const factorCode = params.data['FACTOR'];
                let factorType = 'SOLID'; // Valor por defecto
                
                if (factorCode) {
                  const factor = this.emissionFactorOptions().find(ef => ef.Id === factorCode);
                  if (factor && factor.Type) {
                    factorType = factor.Type;
                  }
                }
                
                return {
                  values: itemsMap[factorType] || []
                };
              };
            }
          }
        }

        if (def.Type === 'emission_factor') {
          colDef.cellRenderer = FactorSelect;
          colDef.cellRendererParams = {
            options: this.emissionFactorOptions(),
          };
          colDef.editable = false;
          colDef.minWidth = 350;
        }

        if (def.Type === 'name') {
          colDef.minWidth = 200;
        }

        if (def.Type === 'boolean') {
          colDef.cellEditor = 'agSelectCellEditor';
          colDef.cellEditorParams = {
            values: [true, false],
          };
        }

        columnDefs.push(colDef);
      });
    }

    // Agregar columnas de MeasurementDefinition
    if (formula.MeasurementDefinition) {
      formula.MeasurementDefinition.forEach(def => {
        const headerName = this.getHeaderLabel(def.Code);
        const colDef: ColDef = {
          field: def.Code,
          headerName: headerName,
          type: 'valueColumn',
          ...this.getColumnWidthOptions(def, headerName, 120),
          editable: true,
          cellEditorParams: {
            getValidationErrors: (params: any) => {
                let { value } = params;
                
                if (def.Type === 'number' || def.Type === 'percentage') {
                  value = this.normalizeToZero(value);
                  params.value = value;
                }

                if (def.Type === 'percentage_string') {
                  const error = this.validatePercentageString(value);
                  if (error) return error;
                }

                if (def.Required && !value && value !== 0) {
                  return ['Campo requerido.'];
                }

                return null;
            },
          },
          valueParser: (params) => {
            if (def.Type === 'number' || def.Type === 'percentage') {
              return this.normalizeToZero(params.newValue);
            }
            return params.newValue;
          },
          valueFormatter: (params: any) => {
            const val = (def.Type === 'number' || def.Type === 'percentage') 
              ? this.normalizeToZero(params.value) 
              : params.value;
            return this.setPercentageValue(val, def.Type);
          }
        };

        columnDefs.push(colDef);
      });
    }

    // Agregar columnas de ResultDefinition
    if (formula.ResultDefinition) {
      formula.ResultDefinition.forEach(def => {
        const headerName = this.getHeaderLabel(def.Code);
        const colDef: ColDef = {
          field: def.Code,
          headerName: headerName,
          ...this.getColumnWidthOptions(def, headerName, 130),
          editable: false,
          type: 'valueColumn',
          cellClass: 'calculated-col',
          cellStyle: { backgroundColor: '#f0f0f0' },
          valueFormatter: (params: any) => {
            if (def.Type === 'boolean') return this.formatBooleanValue(params.value);
            return this.setPercentageValue(params.value, def.Type);
          }
        };

        colDef.valueGetter = (params: any) => {
          if (def.Default && def.Default !== 'empty') {
            return def.Default;
          }
          // Pre-calc ya almacenó el valor redondeado correcto; retornarlo directamente.
          if (params.data[def.Code] !== undefined) {
            return params.data[def.Code];
          }
          // Fallback: evaluar si pre-calc aún no corrió (edge case de inicialización).
          const result = this.evaluateFormula(def.Formula, params.data);
          params.data[def.Code] = result;
          return result;
        };

        columnDefs.push(colDef);
      });
    }

    return columnDefs;
  }

  // Elimina todas las llamadas round(expr, n) de una fórmula devolviendo solo expr.
  // Usa un parser de profundidad para manejar paréntesis anidados correctamente.
  private stripRoundFunctions(formula: string): string {
    let result = formula;
    const roundPattern = /round\(/g;
    let match: RegExpExecArray | null;

    while ((match = roundPattern.exec(result)) !== null) {
      const roundStart = match.index;
      const innerStart = roundStart + 'round('.length;

      // Avanzar hasta encontrar el ')' que cierra este round(
      let depth = 1;
      let i = innerStart;
      while (i < result.length && depth > 0) {
        if (result[i] === '(') depth++;
        else if (result[i] === ')') depth--;
        i++;
      }
      // i apunta al carácter después del ')' de cierre
      const innerContent = result.slice(innerStart, i - 1);

      // Encontrar la última coma a profundidad 0 para separar expr del argumento de precisión
      let lastTopLevelComma = -1;
      let commaDepth = 0;
      for (let j = 0; j < innerContent.length; j++) {
        if (innerContent[j] === '(' || innerContent[j] === '[') commaDepth++;
        else if (innerContent[j] === ')' || innerContent[j] === ']') commaDepth--;
        else if (innerContent[j] === ',' && commaDepth === 0) lastTopLevelComma = j;
      }

      const expr = lastTopLevelComma !== -1
        ? innerContent.slice(0, lastTopLevelComma).trim()
        : innerContent.trim();

      result = result.slice(0, roundStart) + expr + result.slice(i);
      // Reiniciar búsqueda desde el inicio ya que el string cambió de longitud
      roundPattern.lastIndex = 0;
    }

    return result;
  }

  // Devuelve una copia de row donde los codes presentes en rawValues usan el valor crudo (sin round).
  // Esto permite que fórmulas dependientes calculen con precisión completa.
  private buildCalcData(
    row: Record<string, unknown>,
    rawValues: Record<string, unknown>
  ): Record<string, unknown> {
    return { ...row, ...rawValues };
  }

  /**
   * Pre-calcula los valores de ResultDefinition sobre cada fila de rowData en orden
   * secuencial, de forma que cuando el valueGetter se ejecute, params.data ya contenga
   * los resultados de las columnas previas y no lea valores "atrasados".
   */
  private preCalculateResultDefinitions(
    formula: EmissionSourceFormulaModel,
    rowData: any[] | null
  ): void {
    if (!formula.ResultDefinition || !rowData || rowData.length === 0) return;

    for (const row of rowData) {
      // rawValues acumula los valores sin round de cada columna procesada,
      // para que las fórmulas dependientes usen precisión completa como insumo.
      const rawValues: Record<string, unknown> = {};

      for (const def of formula.ResultDefinition) {
        if (def.Default && def.Default !== 'empty') {
          rawValues[def.Code] = def.Default;
          row[def.Code] = def.Default;
          continue;
        }

        const calcData = this.buildCalcData(row as Record<string, unknown>, rawValues);
        rawValues[def.Code] = this.evaluateFormula(this.stripRoundFunctions(def.Formula), calcData);
        // Guardar el valor redondeado para display y para el guardado al backend
        row[def.Code] = this.evaluateFormula(def.Formula, calcData);
      }
    }
  }

  validatePercentageString(value: any): string[] | null {
    if (value !== null && value !== undefined && value !== '') {
      const regex = /^-?\d*\.?\d+$/;
      if (!regex.test(String(value).trim())) {
        return ['El valor debe ser numérico y usar punto (.) como separador decimal.'];
      }
    }
    return null;
  }

  // Normalizar un valor a 0 en caso de ser nulo, indefinido, vacío o NaN
  normalizeToZero(value: any): any {
    if (
      value === null || 
      value === undefined || 
      (typeof value === 'string' && value.trim() === '') || 
      Number.isNaN(value) || 
      Number.isNaN(Number(value))
    ) {
      return 0;
    }
    return value;
  }

  formatBooleanValue(value: any): any {
    if (value === true || value === 'true' || value === 1 || value === '1') return 'Sí';
    if (value === false || value === 'false' || value === 0 || value === '0') return 'No';
    return value;
  }

  setPercentageValue(value: any, type: string): any {
    if (value === null || value === undefined || value === '') return value;

    if (type === "percentage") {
      const num = Number(value) * 100;
      const rounded = Number(num.toFixed(2));
      return `${rounded} %`;
    }
    
    if (type === "percentage_string") {
      return `${value} %`;
    }

    return value;
  }

  evaluateFormula(formula: string, data: any, pcgOverride?: string) {
    if (formula) {
      // Limpiar saltos de línea y tabs
      let cleanFormula = formula.replace(/\n/g, ' ').replace(/\t/g, ' ');

      // Se agrega override para PCG cuando se ejecuta el método onSave
      const scope = {
        ...this.filters(),
        ...(pcgOverride ? { pcg: pcgOverride } : {}),
        data: {
          ...(data || {}),
          // Asegurar que UNIT esté disponible en el scope si existe en la definición de la fórmula
          UNIT: data?.UNIT || this.formula()?.Unit
        }
      };

      // Resolver LOOKUP
      if (cleanFormula.includes('LOOKUP')) {
        const lookupRegex = /LOOKUP:[^\s?:)]+(?::[^\s?:)]+)*/g;
        cleanFormula = cleanFormula.replace(lookupRegex, (match) => {
          const result = this.evaluateLookup(match, data, pcgOverride);
          if (result === undefined || result === null) return '0';
          
          // Si el resultado es texto, se envuelve en comillas
          // para que mathjs lo interprete como un string literal y no falle la evaluación.
          if (typeof result === 'string' && isNaN(Number(result))) {
            return `'${result}'`;
          }
          return result.toString();
        });
      }

      // Resolver CountMeasurementeDefinition
      if (cleanFormula.includes('CountMeasurementDefinition')) {
        cleanFormula = this.processCountMeasurementDefinition(cleanFormula);
      }

      try {
        // Resolver variables {{variable}}
        cleanFormula = this.resolveTemplateVars(cleanFormula, data, pcgOverride);

        return this.math.evaluate(cleanFormula, scope);
      } catch (e) {
        return 0;
      }
    }
  }

  // Obtener valores externos a la data de la tabla, de diccionarios predefinidos
  private evaluateLookup(formula: string, data: any, pcgOverride?: string) {
    const { dictionary, params } = this.parseLookupFormula(formula);

    switch (dictionary) {
      case 'GlobalWarmingPotential':
        return this.lookupGlobalWarmingPotential(params, data, pcgOverride);

      case 'EmissionFactor':
        return this.lookupEmissionFactor(params, data, pcgOverride);

      case 'Conversion':
        return this.lookupConversion(params, data, pcgOverride);

      default:
        return;
    }
  }

  // Procesar CountMeasurementDefinition reemplazándolo con las keys de MeasurementDefinition
  private processCountMeasurementDefinition(formula: string): string {
    const measurementCodes = this.formula()?.MeasurementDefinition?.map(def => def.Code) || [];

    if (measurementCodes.length === 0) {
      return formula;
    }

    // Crear la cadena de reemplazo: data['MONTH-1'], data['MONTH-2'], ...
    const replacement = measurementCodes.map(code => `data['${code}']`).join(', ');

    // return formula.replace(/CountMeasurementDefinition/g, `[${replacement}]`); // Si se requiere como array
    return formula.replace(/CountMeasurementDefinition/g, replacement);
  }

  // Obtener key según valor de fórmula
  private resolveFilterVariable(expression: string, data: any, pcgOverride?: string): any {
    const key = expression.replace(/{{|}}/g, '');
    const filters = this.filters();

    // Mapear campos especiales
    if (key === 'gwp') {
      const fromFilters = pcgOverride ?? filters?.['pcg' as keyof FormulaSearchModel];
      const fromData = (data as Record<string, any>)?.[key];
      return fromFilters ?? fromData;
    } else if (key === 'unit') {
      return (data as Record<string, any>)?.['UNIT'];
    }

    const fromFilters = filters?.[key as keyof FormulaSearchModel];
    const fromData = (data as Record<string, any>)?.[key];
    return fromFilters ?? fromData;
  }

  private handleMissingValue(onMissing: string | undefined, data?: any, pcgOverride?: string) {
    if (!onMissing) return undefined;

    // Si onMissing contiene una expresión condicional compleja (contiene ? y :)
    if (onMissing.includes('?') && onMissing.includes(':')) {
      try {
        // Crear un scope temporal para evaluar la expresión
        const tempScope: any = {
          ...this.filters(),
          data
        };

        // Resolver variables en la expresión
        let processedExpression = onMissing.replace(/{{(.*?)}}/g, (_, key) => {
          const value = this.resolveFilterVariable(`{{${key}}}`, data, pcgOverride);
          if (typeof value === 'string') {
            const safeKey = `var_${key.trim().replace(/[^a-zA-Z0-9]/g, '_')}`;
            tempScope[safeKey] = value;
            return safeKey;
          }
          return value ?? 'null';
        });

        // Evaluar la expresión condicional
        const result = this.math.evaluate(processedExpression, tempScope);
        return result;
      } catch (e) {
        return 0;
      }
    }

    // Comportamiento original para casos simples
    if (onMissing === '0' || onMissing === 'empty') {
      return 0;
    }
    return onMissing;
  }

  // Convertir fórmula en string de LOOKUP a un objeto para extraer las keys
  private parseLookupFormula(formula: string) {
    const parts = formula.split(':');

    // parts[0] === 'LOOKUP'
    const dictionary = parts[1];

    const params: Record<string, string> = {};

    for (let i = 2; i < parts.length; i++) {
      const part = parts[i];
      const equalsIndex = part.indexOf('=');

      if (equalsIndex !== -1) {
        const key = part.substring(0, equalsIndex);
        const value = part.substring(equalsIndex + 1);

        // Manejar casos donde el valor contiene comas (como múltiples filtros)
        // Solo dividir si no estamos en un contexto de variable {{...}} o data[...]
        let finalValue = value;
        if (key === 'byField' && value.includes(',byCode=')) {
          // Mantener el valor completo con las comas para procesarlo después
          finalValue = value;
        }

        params[key] = finalValue;
      }
    }

    return {
      dictionary,
      params
    };
  }

  // Busca el factor de emisión según los parámetros proporcionados
  private lookupEmissionFactor(
    params: Record<string, string>,
    data: any,
    pcgOverride?: string
  ) {
    const {
      byCode,
      byBioFactor,
      list,
      byName,
      useField,
      onMissing
    } = params;

    // Obtener valor de byCode
    const byCodePairs = this.parseByFieldPairs(byCode || byBioFactor, data, 'EmissionFactor', pcgOverride);
    const code = byCodePairs.find(p => p.key === 'byCode')?.value;

    // Buscar en el diccionario para EmissionFactor
    const factor = this.emissionFactorOptions().find(
      ef => ef.Id?.toString() === code?.toString()
    );

    // Si no existe el factor, retornar el valor para empty
    if (!factor) {
      return this.handleMissingValue(onMissing, data);
    }

    // Si fórmula no tiene parámetro list
    if (!list || list !== 'gases') {
      let result: any;

      switch (useField) {
        case 'code': {
          result = factor.Code ?? this.handleMissingValue(onMissing, data, pcgOverride);
          break;
        }
        case 'uncertainty': {
          result = factor.Uncertainty ?? this.handleMissingValue(onMissing, data, pcgOverride);
          break;
        }
        case 'isBiomass': {
          result = factor.IsBiomass ?? this.handleMissingValue(onMissing, data, pcgOverride);
          break;
        }
        case 'unit': {
          result = factor.Unit ?? this.handleMissingValue(onMissing, data, pcgOverride);
          break;
        }
        default: {
          // Si no se especifica useField válido, retornar el valor por defecto
          result = this.handleMissingValue(onMissing, data, pcgOverride);
          break;
        }
      }

      return result;
    }

    // Si se busca en la lista de gases (list=gases)
    if (list === 'gases') {
      // Obtener el nombre del gas dinámicamente
      const gasName = this.resolveParamValue(byName, data, pcgOverride);

      let gasMatch: GasesModel | undefined = {} as GasesModel;

      if (byCode) {
        // Buscar el gas específico dentro del factor
        gasMatch = factor.Gases.find(g => g.Name === gasName);
      }
      else if (byBioFactor) {
        // Buscar el gas específico dentro del objeto BioFactor de factor
        gasMatch = factor.BioFactor?.Gases.find(g => g.Name === gasName);
      }

      if (!gasMatch) {
        return this.handleMissingValue(onMissing, data, pcgOverride);
      }

      // Retornar el campo solicitado del gas
      return gasMatch.Value ?? this.handleMissingValue(onMissing, data, pcgOverride);
    }

    // Si llega aquí, no se encontró un caso válido
    return this.handleMissingValue(onMissing, data, pcgOverride);
  }

  // Busca el PGW según los parámetros proporcionados
  private lookupGlobalWarmingPotential(
    params: Record<string, string>,
    data: any,
    pcgOverride?: string
  ) {
    const {
      byReference,
      list,
      byName,
      useField,
      onMissing
    } = params;

    // Obtener el valor de la referencia de los filtros
    const referenceId = this.resolveParamValue(byReference, data, pcgOverride);

    // Buscar referencia en diccionario de GWP para obtener objeto
    const reference = this.globalWarmingPotentialOptions()
      .find(gwp => gwp.Id === referenceId);

    if (!reference || list !== 'gases') {
      return this.handleMissingValue(onMissing, data, pcgOverride) ?? 0;
    }

    // Obtener el valor del name a buscar en listado de gases
    const name = this.resolveParamValue(byName, data, pcgOverride);
    const match = reference.Gases.find(g => g.Name === name);

    if (!match) {
      return this.handleMissingValue(onMissing, data, pcgOverride) ?? 0;
    }

    const field = (useField as GasPotentialField) ?? 'value';
    const mappedKey = this.gasFieldMap[field];

    return match[mappedKey] ?? (this.handleMissingValue(onMissing, data, pcgOverride) ?? 0);
  }

  // Busca la conversión según los parámetros proporcionados
  private lookupConversion(params: Record<string, string>, data: any, pcgOverride?: string) {
    const {
      type: rawType,
      list,
      byField,
      byCode,
      useField,
      onMissing
    } = params;

    // Determinar el type
    const typeValue = this.resolveParamValue(rawType, data, pcgOverride);

    if (!typeValue) {
      return this.handleMissingValue(onMissing, data);
    }

    // Buscar en el diccionario de Conversion por Type
    const conversionEntry = this.conversionOptions().find(c => c.Type === typeValue);

    if (!conversionEntry) {
      return this.handleMissingValue(onMissing, data, pcgOverride);
    }

    // Acceder a la lista Values
    if (list === 'values') {
      let matchedValueRow: any = null;

      // Determinar el método de búsqueda: byField o byCode

      // Búsqueda con múltiples filtros (detectado por coma en byField)
      if (byField && byField.includes(',')) {
        const filterPairs = this.parseByFieldPairs(byField, data, 'Conversion', pcgOverride);

        matchedValueRow = conversionEntry.Values.find(valueRow => {
          // Todos los pares key-value deben coincidir exactamente
          return filterPairs.every(({ key, value }) => {
            // Mapear claves de filterPairs a propiedades de conversionEntry
            const actualKey = key === 'reference' ? 'Reference' : key === 'byCode' ? 'Code' : key;
            const actualValue = (valueRow as any)[actualKey];
            return actualValue != null && String(actualValue) === String(value);
          });
        });
      }
      // Búsqueda simple por byField
      else if (byField) {
        const filterPairs = this.parseByFieldPairs(byField, data, 'Conversion', pcgOverride);

        matchedValueRow = conversionEntry.Values.find(valueRow => {
          return filterPairs.every(({ key, value }) => {
            const actualValue = (valueRow as any)[key];
            return actualValue != null && String(actualValue) === String(value);
          });
        });
      }
      // Búsqueda simple por byCode
      else if (byCode) {
        const codeValue = this.resolveParamValue(byCode, data, pcgOverride);

        if (codeValue !== undefined && String(codeValue) !== 'undefined') {
          matchedValueRow = conversionEntry.Values.find(v =>
            String(v.Code) === String(codeValue)
          );
        }
      }

      // Si no se encuentra el objeto, retornar onMissing
      if (!matchedValueRow) {
        return this.handleMissingValue(onMissing, data, pcgOverride);
      }

      // Retornar el campo solicitado (por defecto Value)
      const fieldToReturn = useField ?? 'value';
      const mappedKey = this.conversionFieldMap[fieldToReturn as ConversionField] ?? 'Value';
      const result = matchedValueRow[mappedKey] ?? this.handleMissingValue(onMissing, data, pcgOverride);

      return result;
    }

    // Si no tiene list=values, retornar onMissing
    return this.handleMissingValue(onMissing, data);
  }

  /**
   * Parsea una string de filtros en múltiples pares key-value
   * Formatos soportados:
   * - Simple: "reference={{reference}}"
   * - Múltiple: "reference={{reference}},byCode=data['UNIT']"
   * - Implícito: "value" (se asume key='byCode')
   *
   * Usa resolveSingleValue para resolver cada valor individual
   */
  private parseByFieldPairs(
    byField: string,
    data: any,
    context?: 'EmissionFactor' | 'Conversion' | 'GlobalWarmingPotential',
    pcgOverride?: string
  ): Array<{ key: string; value: any }> {

    const pairs: Array<{ key: string; value: any }> = [];

    if (!byField) return pairs;

    const filterParts = byField.split(',');

    for (const part of filterParts) {
      const trimmed = part.trim();

      let key: string;
      let rawValue: string;

      // key=value
      if (trimmed.includes('=')) {
        const eqIndex = trimmed.indexOf('=');
        key = trimmed.slice(0, eqIndex).trim();
        rawValue = trimmed.slice(eqIndex + 1).trim();
      }
      // solo valor → clave implícita
      else {
        key = 'byCode';
        rawValue = trimmed;
      }

      const value = this.resolveSingleValue(rawValue, data, context, pcgOverride);

      pairs.push({ key, value });
    }

    return pairs;
  }

  private resolveParamValue(rawValue?: string, data?: any, pcgOverride?: string): any {
    if (!rawValue) return undefined;
    return this.resolveSingleValue(rawValue, data, undefined, pcgOverride);
  }

  // Resolver un valor individual: {{variable}}, data['field'], o literal
  private resolveSingleValue(rawValue: string, data?: any, context?: 'EmissionFactor' | 'Conversion' | 'GlobalWarmingPotential', pcgOverride?: string): any {
    // {{var}}
    if (rawValue.startsWith('{{') && rawValue.endsWith('}}')) {
      const varName = rawValue.slice(2, -2);
      // Cuando context es EmissionFactor entonces {{code}} se refiere a la key emissionSource
      if (context === 'EmissionFactor' && varName === 'code') {
        return this.filters()?.emissionSource;
      }
      if (varName === 'gwp') {
        return pcgOverride ?? this.filters()?.pcg;
      }
      else {
        return data?.[varName] ?? this.filters()?.[varName as keyof FormulaSearchModel];
      }
    }
    // data['FIELD']['SUBFIELD']
    else if (rawValue.startsWith("data['") && rawValue.includes("']['") && rawValue.endsWith("']")) {
      const content = rawValue.slice(6, -2);
      const parts = content.split("']['");
      const varName = parts[0];
      const subField = parts[1];

      if (varName === 'FACTOR' && subField === 'Type') {
        const factorId = data?.[varName];
        if (factorId) {
          const factor = this.emissionFactorOptions().find(ef => ef.Id?.toString() === factorId?.toString());
          if (factor && factor.Type) {
            return factor.Type;
          }
        }
      }
      return data?.[varName]?.[subField];
    }
    // data['FIELD']
    else if (rawValue.startsWith("data['") && rawValue.endsWith("']")) {
      const varName = rawValue.slice(6, -2);
      return data?.[varName];
    }
    // literal
    else {
      return rawValue;
    }
  }

  // Mapear valores de Activity a estructura de la tabla
  mapActivityRowToTableRow(
    activityRow: any,
    formula: EmissionSourceFormulaModel
  ): Record<string, any> {
    const row: Record<string, any> = {};

    const fieldTypesMap = new Map<string, string>();

    // Mapear tipos de ActivityDefinition
    formula.ActivityDefinition?.forEach(def => {
      fieldTypesMap.set(def.Code, def.Type);
    });

    // Mapear tipos de MeasurementDefinition
    formula.MeasurementDefinition?.forEach(def => {
      fieldTypesMap.set(def.Code, def.Type);
    });

    // Mapear tipos de ResultDefinition
    formula.ResultDefinition?.forEach(def => {
      fieldTypesMap.set(def.Code, def.Type);
    });

    // Función auxiliar para convertir valores según su tipo
    const convertValueByType = (key: string, value: any): any => {
      const fieldType = fieldTypesMap.get(key);

      // Si es de tipo number y viene como string, convertir a número
      if ((fieldType === 'number' || fieldType === 'percentage') &&
        typeof value === 'string' &&
        value !== '') {
        const numValue = Number(value);
        return isNaN(numValue) ? 0 : numValue;
      }
      return value;
    };

    // Mapear los campos de Definition a la estructura de fila
    if (activityRow.Definition) {
      Object.keys(activityRow.Definition).forEach(key => {
        const value = activityRow.Definition[key];
        row[key] = convertValueByType(key, value);
      });
    }

    // Mapear los campos de Measurements a la estructura de fila
    if (activityRow.Measurements) {
      Object.keys(activityRow.Measurements).forEach(key => {
        const value = activityRow.Measurements[key];
        row[key] = convertValueByType(key, value);
      });
    }

    // Agregar campos de MeasurementDefinition si no existen
    const addFields = (defs?: { Code: string; Type?: string }[]) => {
      if (!defs) return;

      defs.forEach(def => {
        if (!(def.Code in row)) {
          row[def.Code] = this.getDefaultValueByType(def.Type);
        }
      });
    };

    addFields(formula.ActivityDefinition);
    addFields(formula.MeasurementDefinition);
    addFields(formula.ResultDefinition);

    // Asegurar que UNIT esté presente en la fila para las fórmulas que lo requieran
    if (formula.Unit && row['UNIT'] === undefined) {
      row['UNIT'] = formula.Unit;
    }

    return row;
  }

  private getDefaultValueByType(type?: string) {
    switch (type) {
      case 'number':
        return 0;
      case 'name':
      case 'unit':
      case 'emission_factor':
      case 'text':
        return null;
      default:
        return null;
    }
  }

  // Reemplaza todas las variables {{var}} en una fórmula con sus valores
  resolveTemplateVars(
    formula: string,
    data: any,
    pcgOverride?: string
  ): string {
    return formula.replace(/{{(\w+)}}/g, (match) => {
      const value = this.resolveFilterVariable(match, data, pcgOverride);

      // strings deben ir entre comillas para math.js
      return typeof value === 'string'
        ? `'${value}'`
        : String(value);
    });
  }

  openUploadFiles(row: ActivitiesRowModel) {
    this.openUploadFilesDialog.emit({activity: row, open: true});
  }

  onCellContextMenu(event: CellContextMenuEvent) {
    event.event?.preventDefault();
    event.event?.stopPropagation();

    if (event.rowIndex === undefined || event.rowIndex === null) {
      return
    }

    const row = this.activities()?.Rows?.[event.rowIndex];
    if (row?.Id) {
      this.selectedRow = row;
      event.node.setSelected(true, true);
      
      this.contextMenuItems = [];
      
      if (!row.Id.startsWith('temp-')) {
        this.contextMenuItems.push({
          label: 'Anexos',
          icon: 'pi pi-copy',
          command: () => this.openUploadFiles(this.selectedRow)
        });
      }
      
      this.contextMenuItems.push({
        label: 'Eliminar',
        icon: 'pi pi-trash',
        command: () => this.deleteActivity.emit(this.selectedRow)
      });

      this.contextMenu.show(event.event as MouseEvent); // Mostrar menú
    }
  }

  @HostListener('window:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (!this.shouldInterceptPaste(event)) return;

    const focusedCell = this.agGrid.api.getFocusedCell();
    if (!focusedCell || !this.isMeasurementColumn(focusedCell.column.getColId())) return;

    const pastedData = this.extractClipboardData(event);
    if (!pastedData) return;

    event.preventDefault();

    const hasUpdates = this.applyPastedDataToGrid(pastedData, focusedCell);

    if (hasUpdates) {
      this.forceCalculateAllRows();
      this.tableDataChange.emit(this.rowData || []);
    }
  }

  /**
   * Evalúa si se debe interceptar el evento de pegado.
   */
  private shouldInterceptPaste(event: ClipboardEvent): boolean {
    if (!this.isGridReady || !this.agGrid?.api) return false;

    // Verificar que el foco no esté en un input externo (ej. contenedor externo de la tabla)
    const target = event.target as HTMLElement;
    const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
    const isInsideGrid = target && target.closest('.ag-root-wrapper');

    if (isInput && !isInsideGrid) return false;

    // No interceptar si el usuario está editando la celda activamente
    if (this.agGrid.api.getEditingCells().length > 0) return false;

    return true;
  }

  // Validar si la columna pertenece a MeasurementDefinition (en las cuales se permite el pegado de datos)
  private isMeasurementColumn(colId: string): boolean {
    const measurementDefs = this.formula()?.MeasurementDefinition || [];
    return measurementDefs.some(d => d.Code === colId);
  }

  /**
   * Extrae y formatea los datos del portapapeles.
   * Convierte el texto plano en una matriz bidimensional (filas y columnas) separando por saltos de línea y tabulaciones.
   */
  private extractClipboardData(event: ClipboardEvent): string[][] | null {
    const clipboardData = event.clipboardData?.getData('text/plain');
    if (!clipboardData) return null;

    const rowsText = clipboardData.replace(/\r/g, '').replace(/\n$/, '').split('\n');
    return rowsText.map(row => row.split('\t'));
  }

  /**
   * Coordina la iteración sobre las filas pegadas y las mapea a las filas existentes del grid.
   * Inicia el proceso a partir de la celda actualmente enfocada.
   */
  private applyPastedDataToGrid(pastedData: string[][], focusedCell: any): boolean {
    const allCols = this.agGrid.api.getColumns()?.filter(c => c.isVisible()) || [];
    const startColIndex = allCols.findIndex(c => c.getColId() === focusedCell.column.getColId());
    const startRowIndex = focusedCell.rowIndex;

    if (startColIndex === -1 || startRowIndex === null) return false;

    let hasUpdates = false;

    for (let r = 0; r < pastedData.length; r++) {
      const targetRowIndex = startRowIndex + r;
      const rowNode = this.agGrid.api.getDisplayedRowAtIndex(targetRowIndex);
      
      if (!rowNode || !rowNode.data) continue;

      const pastedRow = pastedData[r];
      const rowUpdated = this.applyPastedRowToNode(pastedRow, rowNode, startColIndex, allCols);
      if (rowUpdated) hasUpdates = true;
    }

    return hasUpdates;
  }

  /**
   * Procesa una fila individual pegada, celda por celda.
   * Se asegura de que los datos solo se inserten en las columnas permitidas (MeasurementDefinition).
   */
  private applyPastedRowToNode(pastedRow: string[], rowNode: any, startColIndex: number, allCols: any[]): boolean {
    let hasUpdates = false;
    const measurementDefs = this.formula()?.MeasurementDefinition || [];

    for (let c = 0; c < pastedRow.length; c++) {
      const targetColIndex = startColIndex + c;
      if (targetColIndex >= allCols.length) continue;

      const targetCol = allCols[targetColIndex];
      const colId = targetCol.getColId();
      const def = measurementDefs.find(d => d.Code === colId);

      if (!def) continue;

      rowNode.data[colId] = this.parsePastedValue(pastedRow[c], def.Type);
      hasUpdates = true;
    }

    return hasUpdates;
  }

  /**
   * Valida, convierte y normaliza el valor pegado según el tipo de dato de la columna.
   * Maneja valores nulos, vacíos o textos inválidos convirtiéndolos en 0 para evitar errores de tipo NaN.
   */
  private parsePastedValue(rawValue: string, type: string): any {
    if (type !== 'number' && type !== 'percentage') {
      return rawValue;
    }

    let value = this.normalizeToZero(rawValue);
    if (typeof value === 'string' && value !== '') {
      const parsedNum = Number(value);
      value = isNaN(parsedNum) ? 0 : parsedNum;
    }
    
    return value;
  }
}